/* Keeps the cart's Klasse attribute in step with the class chosen on the
   landing page.

   The class is picked right after the access code (assets/school-gate.js),
   which writes it to the cart and stores it in localStorage. If that cart
   request never landed - offline for a moment, a cart cookie replaced since -
   the cart would go to checkout without a class. So the cart page reads the
   stored value, fills the hidden `attributes[Klasse]` field with it (that alone
   is enough for the checkout submit to carry it) and writes it back to the cart
   in the background.

   Wrapped in a custom element rather than an inline script because the cart
   section re-renders itself on every quantity change: script tags injected that
   way never execute, while custom elements upgrade on their own. The cart
   drawer refreshes the same way, and it carries this field too, so a checkout
   straight from the drawer carries the class without visiting the cart page. */
(function () {
  "use strict";

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /* The handle of the school unlocked last, so a device that has seen two
     schools reads the class of the one it is shopping. Falls back to any stored
     class, for a visitor whose `skole:active` predates it. */
  function storedKlasse() {
    const active = read("skole:active");
    if (active) {
      const value = read("skole:" + active + ":klasse");
      if (value) return value;
    }

    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && /^skole:.+:klasse$/.test(key)) {
          const value = window.localStorage.getItem(key);
          if (value) return value;
        }
      }
    } catch (e) {}

    return null;
  }

  class KlasseField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("[data-klasse-input]");
      if (!this.input) return;

      this.name = this.getAttribute("data-name") || "Klasse";
      this.line = this.querySelector("[data-klasse-line]");
      this.value = this.querySelector("[data-klasse-value]");

      this.pointChangeLink();

      const stored = storedKlasse();
      const onCart = this.input.value;

      // Neither source knows the class - the browser was cleared between the
      // gate and here. An order with no class cannot be delivered, so ask for
      // it again; the cart survives the trip.
      //
      // Only the cart page's copy does this (data-bounce). The same field is
      // rendered in the cart drawer, which is on every template, and a policy
      // page or a product page must not throw its reader back to the gate just
      // because nothing has been unlocked on this device yet.
      if (!stored && !onCart) {
        if (this.hasAttribute("data-bounce")) window.location.replace("/");
        return;
      }

      if (!stored) return;
      if (onCart === stored) return;

      /* The two disagree, so one of them has to give, and it has to be all
         three of these together.

         This used to push the stored class to the cart and leave the hidden
         field and the line alone, which was worse than doing nothing: the
         hidden field is what the checkout submit carries, so the push was
         undone by the very next click, and the line on screen still named the
         class the student had just changed away from.

         The stored value wins. Both are written by the gate in the same breath,
         so they only come apart when the gate's cart request did not land -
         which is the exact case this file exists for, and there the browser is
         the one holding the newer answer. */
      this.input.value = stored;
      if (this.value) this.value.textContent = stored;
      if (this.line) this.line.hidden = false;
      this.push(stored);
    }

    /* "Endre" goes to the gate, and points at the school's own link - the same
       `?skole=<handle>` a school hands out - rather than the plain root, so it
       opens on the class step with the class this browser chose already
       selected.

       The gate itself now opens at the class step for a device holding both a
       token and a class (assets/school-gate.js), so the plain root would
       usually get there too. This still earns its keep for the case that does
       not: a token but no class stored, where the root shows the code step and
       `?skole=` does not.

       Nothing is given away by it: the parameter is built from `skole:active`,
       which is only ever written next to an unlock token, and this link is
       rendered inside a cart the visitor reached through the gate. A browser
       that has not unlocked anything keeps the plain root and meets the code. */
    pointChangeLink() {
      const link = this.querySelector("[data-klasse-change]");
      if (!link) return;

      const active = read("skole:active");
      if (!active || !read("skole:" + active)) return;

      link.href = "/?skole=" + encodeURIComponent(active);
    }

    /* Fire and forget: the hidden field already carries the value to checkout,
       this only brings the cart record itself in line. */
    push(klasse) {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

      fetch(root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { [this.name]: klasse } }),
      }).catch(() => {});
    }
  }

  if (!customElements.get("klasse-field")) {
    customElements.define("klasse-field", KlasseField);
  }
})();
